import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  ArrowLeft,
  Eraser,
  ExternalLink,
  Eye,
  Loader2,
  Play,
  Search,
  Zap,
} from "lucide-react";
import {
  getEtlProcessesForPage,
  STOCK_ETL_PROCESSES,
  STOCK_PAGE_TYPES,
  type GetPayloadResponse,
  type PurgeStockPageResponse,
  type ResolveStockResponse,
  type StockEtlProcessDef,
  type StockEtlProcessKey,
  type StockPageTypeKey,
  type UpdateStockTabResponse,
} from "../../../../../shared/index";
import { api, ApiError, isLikelyTimeoutError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";

const TICKER_RE = /^[a-z0-9.:-]+$/;

// Widen the literal unions so optional/branded fields are uniformly typed.
const ALL_PROCESSES: readonly StockEtlProcessDef[] = STOCK_ETL_PROCESSES;

function describeError(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong";
}

function prettyPayload(res: GetPayloadResponse): string {
  if (!res.payloadText) return "";
  if (res.truncated) return res.payloadText;
  try {
    return JSON.stringify(JSON.parse(res.payloadText), null, 2);
  } catch {
    return res.payloadText;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function DataStockEtlPage() {
  const [tickerInput, setTickerInput] = useState("");
  const [resolved, setResolved] = useState<ResolveStockResponse | null>(null);

  const [processKey, setProcessKey] = useState<StockEtlProcessKey | null>(null);
  const [processQuery, setProcessQuery] = useState("");
  const [listingId, setListingId] = useState<number | null>(null);

  const [pageKey, setPageKey] = useState<StockPageTypeKey | null>(null);
  const [pageQuery, setPageQuery] = useState("");
  const [viewed, setViewed] = useState<GetPayloadResponse | null>(null);
  const [purgeResult, setPurgeResult] = useState<PurgeStockPageResponse | null>(null);
  const [tabResult, setTabResult] = useState<UpdateStockTabResponse | null>(null);
  const [confirmingFullRun, setConfirmingFullRun] = useState(false);

  const ticker = tickerInput.trim().toLowerCase();
  const tickerValid = TICKER_RE.test(ticker);
  const process = ALL_PROCESSES.find((p) => p.key === processKey) ?? null;
  const pageType = STOCK_PAGE_TYPES.find((p) => p.key === pageKey) ?? null;

  const needsListing = process?.idKind === "stockListingId";
  const canRun =
    resolved !== null && process !== null && (!needsListing || listingId !== null);
  const canUsePage = tickerValid && pageType !== null;

  // Processes feeding the selected tab — the "update this tab" work list. It
  // can be empty: some tabs are served straight from the aggregate.
  const tabProcesses = useMemo(
    () => (pageType ? getEtlProcessesForPage(pageType.key) : []),
    [pageType]
  );
  // Running a tab's ETL needs a resolved stockId, unlike a bare purge.
  const canUpdateTab = resolved !== null && pageType !== null;

  const processOptions = useMemo<ComboboxOption<StockEtlProcessKey>[]>(() => {
    const q = processQuery.trim().toLowerCase();
    return ALL_PROCESSES.filter(
      (p) => !q || p.label.toLowerCase().includes(q) || p.key.includes(q)
    ).map((p) => ({
      key: p.key,
      label: p.label,
      value: p.key as StockEtlProcessKey,
    }));
  }, [processQuery]);

  const pageOptions = useMemo<ComboboxOption<StockPageTypeKey>[]>(() => {
    const q = pageQuery.trim().toLowerCase();
    return STOCK_PAGE_TYPES.filter(
      (p) => !q || p.label.toLowerCase().includes(q) || p.bffSlug.includes(q)
    ).map((p) => ({ key: p.key, label: p.label, value: p.key }));
  }, [pageQuery]);

  // ── Resolve ticker → stockId + listings ──
  const resolveMutation = useMutation({
    mutationFn: () => api.data.resolveStock(ticker),
    onSuccess: (data) => {
      setResolved(data);
      // Default to the primary listing for listing-based processes.
      setListingId(data.listings[0]?.stockListingId ?? null);
    },
    onError: (e) => {
      setResolved(null);
      toast.error(
        e instanceof ApiError && e.status === 404
          ? `Ticker "${ticker}" not found`
          : describeError(e)
      );
    },
  });

  // ── Run one ETL process ──
  const runMutation = useMutation({
    mutationFn: (def: StockEtlProcessDef) =>
      api.data.runStockEtl(
        ticker,
        def.key as StockEtlProcessKey,
        def.idKind === "stockListingId" && listingId !== null
          ? { stockListingId: listingId }
          : {}
      ),
    onSuccess: (result, def) => {
      if (result.status === "completed") {
        toast.success(`${def.label}: ${result.message ?? "completed"}`);
      } else if (result.status === "started") {
        toast.info(
          `${def.label}: still running upstream — verify with View payload in a bit; do not re-run.`
        );
      } else {
        toast.error(`${def.label} failed: ${result.message ?? "unknown error"}`);
        return;
      }
      // Point the page-cache section at the page this process feeds.
      if (def.pageKeys.length > 0 && !pageKey) {
        setPageKey(def.pageKeys[0] as StockPageTypeKey);
      }
    },
    onError: (e, def) => {
      if (isLikelyTimeoutError(e)) {
        toast.info(
          `${def.label}: the request timed out but the run may still be going. Verify with View payload before re-running.`
        );
      } else {
        toast.error(`${def.label} failed: ${describeError(e)}`);
      }
    },
  });

  // ── Full update: every ETL process, then every page ──
  const fullRunMutation = useMutation({
    mutationFn: () => api.data.fullStockRun(ticker),
    onSuccess: (data) => {
      setConfirmingFullRun(false);
      toast.info(
        `Full update started for ${ticker.toUpperCase()}: ${data.etlProcessCount} ETL processes, then ${data.pageCount} pages purged. This runs for several minutes — check the audit log for the outcome; do not re-run.`
      );
    },
    onError: (e) => {
      setConfirmingFullRun(false);
      toast.error(`Full update failed to start: ${describeError(e)}`);
    },
  });

  // ── Page cache: view / purge ──
  const viewMutation = useMutation({
    mutationFn: () => api.data.getPayload(ticker, pageType!.key),
    onSuccess: (data) => setViewed(data),
    onError: (e) => toast.error(`View failed: ${describeError(e)}`),
  });

  const purgeMutation = useMutation({
    mutationFn: () => api.data.purgeStockPage(ticker, pageType!.key),
    onSuccess: (data) => {
      setPurgeResult(data);
      const failed = data.purges.filter((r) => !r.ok).length;
      if (failed === 0) {
        toast.success("CDN copies purged.");
      } else {
        toast.error(`${failed} purge(s) failed.`);
      }
    },
    onError: (e) => toast.error(`Purge failed: ${describeError(e)}`),
  });

  // ── Update one tab: its ETL processes, then purge ──
  const tabUpdateMutation = useMutation({
    mutationFn: () =>
      api.data.updateStockTab(
        ticker,
        pageType!.key,
        listingId !== null ? { stockListingId: listingId } : {}
      ),
    onSuccess: (data) => {
      setTabResult(data);
      setPurgeResult(null);
      const etlFailed = data.etl.filter((r) => r.status === "failed").length;
      if (data.status === "started") {
        toast.info(
          `${pageType?.label}: still running upstream — the remaining ETL and the purge continue in the background. Check the audit log; do not re-run.`
        );
        return;
      }
      const purgesFailed = data.purges.filter((r) => !r.ok).length;
      if (etlFailed === 0 && purgesFailed === 0) {
        toast.success(
          `${pageType?.label} updated: ${data.etl.length} ETL process(es), ${data.purges.length} URL(s) purged.`
        );
      } else {
        toast.error(
          `${pageType?.label}: ${etlFailed} ETL failure(s), ${purgesFailed} purge failure(s).`
        );
      }
    },
    onError: (e) => {
      if (isLikelyTimeoutError(e)) {
        toast.info(
          `${pageType?.label}: the request timed out but the update may still be going. Check the audit log before re-running.`
        );
      } else {
        toast.error(`Tab update failed: ${describeError(e)}`);
      }
    },
  });

  const busy =
    resolveMutation.isPending ||
    runMutation.isPending ||
    viewMutation.isPending ||
    purgeMutation.isPending ||
    tabUpdateMutation.isPending ||
    fullRunMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Link
          to="/dashboard/data"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Data
        </Link>
        <h1 className="text-2xl font-bold">Stock ETL</h1>
        <p className="text-sm text-muted-foreground">
          Run one ETL process for one stock, or update a whole tab end to end.
          Many processes end with a page purge on the data platform; for the
          ones that don't, use step 3 afterwards.
        </p>
      </div>

      {/* ── Step 1: ticker ── */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="font-semibold">1. Stock</h2>
        <div className="mt-4 flex max-w-md items-end gap-2">
          <div className="flex-1 space-y-2">
            <Label htmlFor="ticker">Ticker</Label>
            <Input
              id="ticker"
              value={tickerInput}
              onChange={(e) => {
                setTickerInput(e.target.value);
                setResolved(null);
                setPurgeResult(null);
                setTabResult(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && tickerValid) resolveMutation.mutate();
              }}
              placeholder="AAPL"
            />
          </div>
          <Button
            onClick={() => resolveMutation.mutate()}
            disabled={!tickerValid || busy}
          >
            {resolveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Resolve
          </Button>
        </div>
        {tickerInput && !tickerValid && (
          <p className="mt-2 text-xs text-destructive">
            Tickers may only contain letters, digits, dots, colons, and dashes.
          </p>
        )}
        {resolved && (
          <p className="mt-3 text-sm text-muted-foreground">
            stockId{" "}
            <span className="font-mono text-foreground">{resolved.stockId}</span>
            {" · "}
            {resolved.listings.length} listing(s)
          </p>
        )}

        {/* Whole-ticker shortcut: everything, in order, in the background. */}
        <div className="mt-6 rounded-lg border border-warning/50 bg-warning/5 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-medium">Full update</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Run every applicable ETL process for this stock (listing-based
                ones against the primary listing), then purge the HTML pages of
                all {STOCK_PAGE_TYPES.length} tabs.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setConfirmingFullRun(true)}
              disabled={!resolved || busy}
            >
              {fullRunMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Zap className="h-4 w-4" />
              )}
              Full update + purge
            </Button>
          </div>
        </div>
      </div>

      {/* ── Step 2: run ETL ── */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="font-semibold">2. Run ETL process</h2>
        <div className="mt-4 max-w-md space-y-4">
          <div className="space-y-2">
            <Label htmlFor="process">Process</Label>
            <Combobox
              id="process"
              value={processKey}
              displayValue={process?.label ?? ""}
              placeholder="Pick a process…"
              options={processOptions}
              minChars={0}
              onQueryChange={setProcessQuery}
              onSelect={(value) => setProcessKey(value)}
            />
            {process && (
              <div className="space-y-1 text-xs text-muted-foreground">
                <p>{process.description}</p>
                {process.autoPurges ? (
                  <Badge variant="success">auto-purges page</Badge>
                ) : (
                  <Badge variant="warning">no auto-purge — update the tab in step 3</Badge>
                )}
              </div>
            )}
          </div>

          {needsListing && (
            <div className="space-y-2">
              <Label htmlFor="listing">Listing</Label>
              <select
                id="listing"
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                value={listingId ?? ""}
                onChange={(e) =>
                  setListingId(e.target.value ? Number(e.target.value) : null)
                }
                disabled={!resolved || resolved.listings.length === 0}
              >
                {!resolved || resolved.listings.length === 0 ? (
                  <option value="">Resolve a ticker first</option>
                ) : (
                  resolved.listings.map((l) => (
                    <option key={l.stockListingId} value={l.stockListingId}>
                      {l.ticker ?? "?"} — {l.displayName ?? "listing"} (
                      {l.stockListingId}){l.isPrimary ? " ★ primary" : ""}
                    </option>
                  ))
                )}
              </select>
            </div>
          )}

          <Button
            onClick={() => process && runMutation.mutate(process)}
            disabled={!canRun || busy}
          >
            {runMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Run ETL
          </Button>
          {!resolved && (
            <p className="text-xs text-muted-foreground">
              Resolve a ticker first.
            </p>
          )}
        </div>
      </div>

      {/* ── Step 3: one tab, end to end ── */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="font-semibold">3. Update one tab</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick a tab and bring it fully up to date: run every ETL process that
          feeds it, then purge the tab's
          HTML. Or just do the purge, for processes that don't purge on their
          own.
        </p>
        <div className="mt-4 max-w-md space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pageType">Tab</Label>
            <Combobox
              id="pageType"
              value={pageKey}
              displayValue={pageType?.label ?? ""}
              placeholder="Pick a tab…"
              options={pageOptions}
              minChars={0}
              onQueryChange={setPageQuery}
              onSelect={(value) => {
                setPageKey(value);
                setPurgeResult(null);
                setTabResult(null);
              }}
            />
            {pageType && (
              <p className="font-mono text-xs text-muted-foreground">
                website-api /asset/payload?class=stock&amp;tab={pageType.bffSlug}
              </p>
            )}
          </div>

          {pageType && (
            <div className="space-y-2 rounded-lg border bg-muted/30 p-3 text-xs">
              {tabProcesses.length > 0 ? (
                <div>
                  <p className="font-medium">
                    ETL this update runs ({tabProcesses.length}):
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    {tabProcesses.map((p) => p.label).join(", ")}
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground">
                  No per-stock ETL process updates this tab — it's computed
                  elsewhere or served from the aggregate, so updating it is a
                  purge only.
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => viewMutation.mutate()}
              disabled={!canUsePage || busy}
            >
              {viewMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              View payload
            </Button>
            <Button
              onClick={() => tabUpdateMutation.mutate()}
              disabled={!canUpdateTab || busy}
            >
              {tabUpdateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Zap className="h-4 w-4" />
              )}
              Update tab
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setTabResult(null);
                purgeMutation.mutate();
              }}
              disabled={!canUsePage || busy}
            >
              {purgeMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Eraser className="h-4 w-4" />
              )}
              Purge only
            </Button>
          </div>

          {tabResult && (
            <div className="space-y-3 rounded-lg border bg-muted/30 p-4 text-sm">
              {tabResult.status === "started" && (
                <p className="text-warning">
                  Overran the request budget — the remaining ETL and the purge
                  are still running server-side. The audit log has the outcome.
                </p>
              )}
              {tabResult.etl.length > 0 && (
                <div>
                  <p className="font-medium">ETL:</p>
                  <ul className="mt-1 space-y-1">
                    {tabResult.etl.map((r) => (
                      <li key={r.processKey} className="text-xs">
                        <span
                          className={
                            r.status === "failed"
                              ? "text-destructive"
                              : r.status === "started"
                                ? "text-warning"
                                : "text-emerald-600 dark:text-emerald-400"
                          }
                        >
                          {r.status}
                        </span>{" "}
                        <span className="font-medium">{r.label}</span>
                        {r.message && (
                          <span className="text-muted-foreground">
                            {" "}
                            — {r.message}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {tabResult.purges.length > 0 && (
                <div>
                  <p className="font-medium">Purged:</p>
                  <ul className="mt-1 space-y-1">
                    {tabResult.purges.map((r) => (
                      <li key={r.url} className="break-all font-mono text-xs">
                        <span
                          className={
                            r.ok
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-destructive"
                          }
                        >
                          {r.ok ? "OK" : `HTTP ${r.status}`}
                        </span>{" "}
                        {r.url}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {purgeResult && (
            <div className="space-y-2 rounded-lg border bg-muted/30 p-4 text-sm">
              <div>
                <p className="font-medium">Purged:</p>
                <ul className="mt-1 space-y-1">
                  {purgeResult.purges.map((r) => (
                    <li key={r.url} className="break-all font-mono text-xs">
                      <span
                        className={
                          r.ok
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-destructive"
                        }
                      >
                        {r.ok ? "OK" : `HTTP ${r.status}`}
                      </span>{" "}
                      {r.url}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full-update confirm dialog */}
      <Dialog open={confirmingFullRun} onOpenChange={setConfirmingFullRun}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Full update for {ticker.toUpperCase()}?
            </DialogTitle>
            <DialogDescription>
              This runs {ALL_PROCESSES.length} ETL processes against the data
              platform, then purges the HTML pages of all{" "}
              {STOCK_PAGE_TYPES.length} tabs. It
              takes several minutes and runs in the background — the outcome
              goes to the audit log, not this screen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmingFullRun(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => fullRunMutation.mutate()}
              disabled={fullRunMutation.isPending}
            >
              {fullRunMutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Start full update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payload viewer dialog */}
      <Dialog
        open={viewed !== null}
        onOpenChange={(open) => !open && setViewed(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {ticker}/{pageType?.label ?? ""} payload
            </DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>HTTP {viewed?.status}</span>
              <span>{viewed ? formatBytes(viewed.sizeBytes) : ""}</span>
              {viewed?.truncated && (
                <span className="text-warning">truncated to 2 MB</span>
              )}
              {viewed && (
                <a
                  href={viewed.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  Open raw <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </DialogDescription>
          </DialogHeader>
          <pre className="max-h-[60vh] overflow-auto rounded-lg border bg-muted/30 p-4 font-mono text-xs">
            {viewed ? prettyPayload(viewed) : ""}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
